import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import {
  Alert,
  Col,
  DatePicker,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import styled from 'styled-components'
import { LEGAL_FORMALITIES_MONITORING_QUERY } from 'graphql/LegalFormalitiesMonitoring'
import GlobalKpis from './GlobalKpis'
import VolumeOverTime, { Granularity } from './VolumeOverTime'
import ByFormalityType from './ByFormalityType'
import ByLegalForm from './ByLegalForm'
import PerformanceMetrics from './PerformanceMetrics'

const { RangePicker } = DatePicker
const { Title } = Typography

const DEFAULT_FROM = dayjs().subtract(6, 'month').startOf('month')
const DEFAULT_TO = dayjs().endOf('month')

const FORMALITY_TYPE_OPTIONS = [
  { label: 'Tous types', value: null },
  { label: 'Création', value: 'creation-express' },
  { label: 'Création micro', value: 'creation-express-micro' },
  { label: 'Transfert', value: 'transfert' },
  { label: 'Transfert micro', value: 'transfert-micro' },
  { label: 'Approbation des comptes', value: 'approbation-des-comptes' },
]

const Monitoring = () => {
  const [granularity, setGranularity] = useState<Granularity>('month')
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    DEFAULT_FROM,
    DEFAULT_TO,
  ])
  const [formalityType, setFormalityType] = useState<string | null>(null)

  const { data, loading, error } = useQuery(
    LEGAL_FORMALITIES_MONITORING_QUERY,
    {
      variables: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD'),
        granularity,
        formalityType,
      },
      fetchPolicy: 'network-only',
    }
  )

  const monitoring = data?.legalFormalitiesMonitoring

  return (
    <MonitoringContainer>
      <FiltersRow>
        <Title level={5} style={{ margin: 0 }}>
          Monitoring Formalités
        </Title>
        <Space wrap>
          <Select
            value={formalityType}
            onChange={setFormalityType}
            options={FORMALITY_TYPE_OPTIONS}
            style={{ width: 200 }}
            size="middle"
          />
          <RangePicker
            value={dateRange}
            onChange={val => {
              if (val && val[0] && val[1]) {
                setDateRange([val[0], val[1]])
              }
            }}
            format="DD/MM/YYYY"
            allowClear={false}
          />
        </Space>
      </FiltersRow>

      {error && (
        <Alert
          type="error"
          message="Erreur lors du chargement des données de monitoring"
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        {monitoring && (
          <ContentGrid>
            <GlobalKpis kpis={monitoring.globalKpis} />

            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <VolumeOverTime
                  data={monitoring.volumeOverTime}
                  granularity={granularity}
                  onGranularityChange={setGranularity}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {!formalityType && (
                <Col xs={24} lg={12}>
                  <ByFormalityType data={monitoring.byFormalityType} />
                </Col>
              )}
              <Col xs={24} lg={formalityType ? 24 : 12}>
                <ByLegalForm data={monitoring.byLegalForm} />
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <PerformanceMetrics performance={monitoring.performance} />
              </Col>
            </Row>
          </ContentGrid>
        )}
      </Spin>
    </MonitoringContainer>
  )
}

export default Monitoring

const MonitoringContainer = styled.div`
  padding: 16px 0;
`

const FiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`

const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
